import { useState } from 'react'
import { ConfirmModal } from '@/components/ConfirmModal'
import { InfoTooltip } from '@/components/InfoTooltip'
import type { OutputResolutionMap } from '@/types/bundle'
import {
  createBlankOutputEntry,
  createSyntheticOutputKey,
  duplicateOutputEntry,
  isSyntheticOutputKey,
} from '../outputResolutionUtils'
import { OutputResolutionRow } from './OutputResolutionRow'

export interface OutputResolutionSectionProps {
  entries: OutputResolutionMap
  onChange: (entries: OutputResolutionMap) => void
  keyDrafts: Record<string, string>
  onKeyDraftChange: (syntheticKey: string, name: string) => void
  /** Map keys (from resolveOutputResolutionForSave) whose resolved name collides with
   * another entry's - Save is already blocked while this is non-empty, this just
   * tells the offending row(s) which one to visually flag. */
  duplicateKeys?: string[]
  /** Map keys (from resolveOutputResolutionForSave) whose resolved name isn't
   * snake_case (PRD 34) - same "Save already blocked, this just flags the row" role
   * as duplicateKeys. */
  invalidKeys?: string[]
  /** Real field names from this product's required document types - the
   * choosable options for a "This document's field" priority entry. */
  documentFieldOptions: string[]
  serverErrors?: Record<string, Record<string, string>>
}

// Structural mirror of document-types' FieldsTab.tsx - map iteration, add/
// duplicate/delete-via-ConfirmModal, expand/collapse state, one-shot entrance
// animation for rows born this session.
export function OutputResolutionSection({
  entries,
  onChange,
  keyDrafts,
  onKeyDraftChange,
  duplicateKeys,
  invalidKeys,
  documentFieldOptions,
  serverErrors,
}: OutputResolutionSectionProps) {
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set())
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null)
  const [bornKeys, setBornKeys] = useState<Set<string>>(new Set())

  function setExpanded(key: string, isExpanded: boolean) {
    setExpandedKeys((current) => {
      const next = new Set(current)
      if (isExpanded) next.add(key)
      else next.delete(key)
      return next
    })
  }

  function updateEntry(key: string, updater: (entry: OutputResolutionMap[string]) => OutputResolutionMap[string]) {
    onChange({ ...entries, [key]: updater(entries[key]) })
  }

  function handleAdd() {
    const key = createSyntheticOutputKey()
    onChange({ ...entries, [key]: createBlankOutputEntry() })
    setExpanded(key, true)
    setBornKeys((current) => new Set(current).add(key))
  }

  function handleDuplicate(sourceKey: string) {
    const key = createSyntheticOutputKey()
    onChange({ ...entries, [key]: duplicateOutputEntry(entries[sourceKey]) })
    onKeyDraftChange(key, '')
    setExpanded(key, true)
    setBornKeys((current) => new Set(current).add(key))
  }

  function handleConfirmDelete() {
    if (!deleteTarget) return
    const next = { ...entries }
    delete next[deleteTarget]
    onChange(next)
    setExpanded(deleteTarget, false)
    setDeleteTarget(null)
  }

  const keys = Object.keys(entries)
  // Only already-named (locked) entries are offered as a "data.<field>"
  // target - a still-being-named draft has no stable name to reuse yet.
  const realKeys = keys.filter((key) => !isSyntheticOutputKey(key))

  return (
    <section className="flex flex-col gap-4 rounded-control border border-border bg-surface p-4">
      <h2 className="flex items-center gap-1 text-header font-semibold text-text-primary">
        Output resolution
        <InfoTooltip text="Defines every field this product returns in its response and where each field's value should come from." />
      </h2>
      <p className="text-meta text-text-secondary">
        Maps each response field to an ordered list of candidate values, resolved by confidence at
        request time.
      </p>

      <div className="flex flex-col gap-4">
        {keys.map((key) => (
          <OutputResolutionRow
            key={key}
            entryKey={key}
            entry={entries[key]}
            isLocked={!isSyntheticOutputKey(key)}
            draftName={keyDrafts[key] ?? ''}
            onNameChange={(name) => onKeyDraftChange(key, name)}
            onChange={(next) => updateEntry(key, () => next)}
            onDelete={() => setDeleteTarget(key)}
            onDuplicate={() => handleDuplicate(key)}
            expanded={expandedKeys.has(key)}
            onToggleExpanded={() => setExpanded(key, !expandedKeys.has(key))}
            isDuplicateName={duplicateKeys?.includes(key) ?? false}
            isInvalidName={invalidKeys?.includes(key) ?? false}
            documentFieldOptions={documentFieldOptions}
            outputFieldOptions={realKeys.filter((otherKey) => otherKey !== key)}
            serverErrors={serverErrors?.[key]}
            isNew={bornKeys.has(key)}
          />
        ))}

        <button
          type="button"
          onClick={handleAdd}
          className="w-fit rounded-control border border-border px-4 py-2 text-body font-medium text-text-primary hover:bg-surface-raised"
        >
          + Add Output Field
        </button>
      </div>

      <ConfirmModal
        open={deleteTarget !== null}
        title="Delete output field"
        description="This output field will be removed from the draft. It is only persisted on your next Save."
        confirmLabel="Delete"
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </section>
  )
}
